import { useEffect, useState } from 'react';
import { apiGet, apiJson, apiMultipart } from '../api/client';
import { auth } from '../firebase';

const API_BASE = import.meta.env.VITE_API_BASE as string;

// Keep in sync with SKILL_NAME_RE in worker/src/routes/skills.ts.
const SKILL_NAME_RE = /^[A-Za-z][\w-]{1,47}$/;

interface SkillListItem {
  name: string;
  description: string;
  triggers: string[];
  size_bytes: number;
  uploaded_at: number;
  signedUrl: string;
}
interface SkillListResponse {
  skills: SkillListItem[];
}
interface SkillGetResponse {
  skill: {
    name: string;
    description: string;
    triggers: string[];
    size_bytes: number;
    uploaded_at: number;
  };
  body: string;
}

function formatUnix(s: number): string {
  try {
    return new Date(s * 1000).toLocaleString();
  } catch {
    return String(s);
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function SkillsTab() {
  const [skills, setSkills] = useState<SkillListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggersText, setTriggersText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  const [viewing, setViewing] = useState<SkillGetResponse | null>(null);

  const refresh = async (): Promise<void> => {
    setErr(null);
    try {
      const res = await apiGet<SkillListResponse>('/api/skills');
      setSkills(res.skills);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const submit = async (): Promise<void> => {
    setUploadErr(null);
    if (!SKILL_NAME_RE.test(name)) {
      setUploadErr(
        'Name must start with a letter and be 2-48 alphanumerics, hyphens, or underscores.',
      );
      return;
    }
    if (!description.trim()) {
      setUploadErr('Description is required.');
      return;
    }
    if (!file) {
      setUploadErr('SKILL.md file is required.');
      return;
    }
    const triggers = triggersText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const fd = new FormData();
    fd.append('name', name.trim());
    fd.append('description', description.trim());
    fd.append('triggers', JSON.stringify(triggers));
    fd.append('file', file);

    setBusy(true);
    try {
      await apiMultipart('/api/skills', fd);
      setName('');
      setDescription('');
      setTriggersText('');
      setFile(null);
      await refresh();
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const view = async (skillName: string): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      // apiGet can't easily send to /api/skills/:name because of path encoding
      // mixing fine; use it directly.
      const res = await apiGet<SkillGetResponse>(
        `/api/skills/${encodeURIComponent(skillName)}`,
      );
      setViewing(res);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (skillName: string): Promise<void> => {
    if (!confirm(`Delete skill "${skillName}"?`)) return;
    setBusy(true);
    try {
      await apiJson('DELETE', `/api/skills/${encodeURIComponent(skillName)}`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Hint: suppress unused warnings on auth/API_BASE imports that may be
  // useful for future direct links — referenced for type safety.
  void auth;
  void API_BASE;

  return (
    <div className="stack">
      <div className="panel">
        <h3>Upload skill</h3>
        <p className="muted small">
          A skill is a SKILL.md file fetched from R2 and injected into the
          chat when the user types <code>/SkillName</code> or when any of
          the trigger phrases appears in a message.
        </p>
        <div className="stack">
          <label>
            <div className="muted small">Name (alpha-start, 2-48 chars)</div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="MySkill"
            />
          </label>
          <label>
            <div className="muted small">Description</div>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One-line summary of what the skill does."
            />
          </label>
          <label>
            <div className="muted small">
              Triggers (comma-separated phrases, case-insensitive)
            </div>
            <input
              type="text"
              value={triggersText}
              onChange={(e) => setTriggersText(e.target.value)}
              placeholder="use the MySkill skill, do the thing"
            />
          </label>
          <label>
            <div className="muted small">SKILL.md file</div>
            <input
              type="file"
              accept=".md,text/markdown,text/plain"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {uploadErr ? (
            <div className="status-missing">Error: {uploadErr}</div>
          ) : null}
          <div className="row">
            <button
              className="btn-primary"
              onClick={() => void submit()}
              disabled={busy || !file || !name.trim()}
            >
              Upload
            </button>
          </div>
        </div>
      </div>

      <div className="panel">
        <h3>Uploaded skills</h3>
        {err ? <div className="status-missing">Error: {err}</div> : null}
        {skills === null ? (
          <div className="muted">Loading…</div>
        ) : skills.length === 0 ? (
          <div className="muted">No skills uploaded yet.</div>
        ) : (
          <ul className="persona-list">
            {skills.map((s) => (
              <li key={s.name} className="persona-list-item">
                <div>
                  <div className="persona-list-name">{s.name}</div>
                  <div className="muted small">{s.description}</div>
                  <div className="small" style={{ marginTop: 4 }}>
                    {s.triggers.length === 0 ? (
                      <span className="muted">(no triggers)</span>
                    ) : (
                      s.triggers.map((t) => (
                        <span key={t} className="badge" style={{ marginRight: 4 }}>
                          {t}
                        </span>
                      ))
                    )}
                  </div>
                  <div className="muted small">
                    {formatBytes(s.size_bytes)} · uploaded{' '}
                    {formatUnix(s.uploaded_at)}
                  </div>
                </div>
                <div className="row">
                  <button onClick={() => void view(s.name)} disabled={busy}>
                    View
                  </button>
                  <button
                    className="btn-danger small"
                    onClick={() => void remove(s.name)}
                    disabled={busy}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {viewing ? (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setViewing(null);
          }}
        >
          <div className="modal">
            <header className="modal-header">
              <h2>{viewing.skill.name}</h2>
              <button
                className="modal-close"
                onClick={() => setViewing(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <section className="modal-body">
              <p className="muted small">{viewing.skill.description}</p>
              <textarea
                rows={24}
                value={viewing.body}
                readOnly
                spellCheck={false}
              />
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
}
